import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Clock, Loader2, Plus, Trash2 } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const DAYS = [
  { value: 0, fr: 'Lundi', en: 'Monday' },
  { value: 1, fr: 'Mardi', en: 'Tuesday' },
  { value: 2, fr: 'Mercredi', en: 'Wednesday' },
  { value: 3, fr: 'Jeudi', en: 'Thursday' },
  { value: 4, fr: 'Vendredi', en: 'Friday' },
  { value: 5, fr: 'Samedi', en: 'Saturday' },
  { value: 6, fr: 'Dimanche', en: 'Sunday' },
];

export default function EmbassySchedule() {
  const { t, lang } = useI18n();
  const [embassies, setEmbassies] = useState([]);
  const [selectedEmbassy, setSelectedEmbassy] = useState('');
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/embassies').then(r => {
      setEmbassies(r.data);
      if (r.data.length > 0) setSelectedEmbassy(r.data[0].embassy_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedEmbassy) {
      api.get(`/embassies/${selectedEmbassy}/schedule`).then(r => {
        setSlots(r.data.slots || []);
      }).catch(() => setSlots([]));
    }
  }, [selectedEmbassy]);

  const addSlot = () => {
    const usedDays = slots.map(s => s.day_of_week);
    const nextDay = DAYS.find(d => !usedDays.includes(d.value));
    if (!nextDay) { toast.error('Tous les jours sont deja configures'); return; }
    setSlots([...slots, { day_of_week: nextDay.value, start_time: '09:00', end_time: '17:00', slot_duration_min: 30, max_per_slot: 1 }]);
  };

  const removeSlot = (idx) => setSlots(slots.filter((_, i) => i !== idx));

  const updateSlot = (idx, field, value) => {
    const updated = [...slots];
    updated[idx] = { ...updated[idx], [field]: value };
    setSlots(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/embassies/${selectedEmbassy}/schedule`, { slots });
      toast.success('Horaires sauvegardes');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const dayLabel = (val) => {
    const d = DAYS.find(d => d.value === val);
    return d ? (lang === 'en' ? d.en : d.fr) : '';
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="embassy-schedule-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('schedule.title')}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t('schedule.desc')}</p>
      </div>

      {embassies.length > 1 && (
        <Card className="border">
          <CardContent className="p-4">
            <Label>Ambassade</Label>
            <Select value={selectedEmbassy} onValueChange={setSelectedEmbassy}>
              <SelectTrigger data-testid="schedule-embassy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {embassies.map(e => (
                  <SelectItem key={e.embassy_id} value={e.embassy_id}>{e.country} - {e.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      <Card className="border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Clock className="w-5 h-5 text-[#007FFF]" />
            {t('schedule.opening_hours')}
          </CardTitle>
          <CardDescription>{t('schedule.opening_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {slots.sort((a, b) => a.day_of_week - b.day_of_week).map((slot, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-border bg-white" data-testid={`schedule-slot-${idx}`}>
              <div className="w-28">
                <span className="text-sm font-medium">{dayLabel(slot.day_of_week)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Input type="time" value={slot.start_time} onChange={e => updateSlot(idx, 'start_time', e.target.value)} className="w-28" data-testid={`slot-start-${idx}`} />
                <span className="text-muted-foreground">-</span>
                <Input type="time" value={slot.end_time} onChange={e => updateSlot(idx, 'end_time', e.target.value)} className="w-28" data-testid={`slot-end-${idx}`} />
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" value={slot.slot_duration_min} onChange={e => updateSlot(idx, 'slot_duration_min', parseInt(e.target.value) || 30)} className="w-16" min={10} max={120} data-testid={`slot-duration-${idx}`} />
                <span className="text-xs text-muted-foreground">min</span>
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" value={slot.max_per_slot} onChange={e => updateSlot(idx, 'max_per_slot', parseInt(e.target.value) || 1)} className="w-14" min={1} max={20} data-testid={`slot-max-${idx}`} />
                <span className="text-xs text-muted-foreground">max</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeSlot(idx)} data-testid={`remove-slot-${idx}`}>
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          ))}

          {slots.length < 7 && (
            <Button variant="outline" className="w-full border-dashed" onClick={addSlot} data-testid="add-day-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('schedule.add_day')}
            </Button>
          )}
        </CardContent>
      </Card>

      <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        {t('cms.save')}
      </Button>
    </div>
  );
}
